import React, { memo } from 'react';
import Grid from '@mui/material/Grid';
import { Field, useFormikContext } from 'formik';

import { baseServiceStatusTypesEnum } from '../../../../../../api/schemas/serviceStatusTypes';
import { TravelFileServiceForm } from '../ServiceForm';
import AutocompleteByOptionId from '../../../../../../components/AutocompleteByOptionId/AutocompleteByOptionId';
import { Country } from '../../../../../../api/countries/types';
import { DropDownOption } from '../../../../../../types';
import TextFieldsOnBlur from '../../../../../../components/TextFieldsOnBlur';

/**
 * Row 2 — the parts of an address a person actually reads off a voucher, so
 * they get a third of the width each. Country is rendered alongside them and
 * is deliberately not in this list: it is an autocomplete, not a text field.
 */
const PRIMARY_FIELDS = [
    { key: 'city', label: 'City' },
    { key: 'province', label: 'State/Province' }
];

/**
 * Row 3 — postal fields are almost never filled in (0 of 14,869 package
 * services carried one), so they sit below rather than competing for width
 * with City and Country.
 */
const POSTAL_FIELDS = [
    { key: 'postalCode', label: 'Postal Code' },
    { key: 'postalBox', label: 'Postal Box' }
];
type Props = {
    countryOptions: Country[] | DropDownOption[];
    startFieldName?: string;
    isCountryFullOption?: boolean;
};
const AddressFields: React.FC<Props> = ({
    countryOptions,
    startFieldName = '',
    isCountryFullOption
}: Props) => {
    const { values } = useFormikContext<TravelFileServiceForm>();
    const fullWidth = true;
    const disabled /* disableService */ = values.status === baseServiceStatusTypesEnum.CANCELLED;

    // Layout: the street address gets its own full-width row, then
    // City / State / Country share a row of thirds, then the postal fields.
    // Previously everything after the address was squeezed into a 7-of-12
    // column and then split into thirds again, leaving City and Country about
    // 19% of the dialog each — too narrow to read a city name in.
    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                <TextFieldsOnBlur
                    fields={[
                        {
                            name: `${startFieldName}address`,
                            label: 'Address',
                            helperText: '',
                            fullWidth,
                            disabled,
                            rows: 2,
                            multiline: true
                        }
                    ]}
                />
            </Grid>

            {PRIMARY_FIELDS.map(({ key, label }) => (
                <Grid key={key} item xs={12} sm={4}>
                    <TextFieldsOnBlur
                        fields={[
                            { name: `${startFieldName}${key}`, label, helperText: '', fullWidth, disabled }
                        ]}
                    />
                </Grid>
            ))}
            <Grid item xs={12} sm={4}>
                <Field
                    component={AutocompleteByOptionId}
                    name={`${startFieldName}country`}
                    label="Country"
                    options={countryOptions}
                    disabled={disabled}
                    helperText=" "
                    fullOption={isCountryFullOption}
                />
            </Grid>

            {POSTAL_FIELDS.map(({ key, label }) => (
                <Grid key={key} item xs={12} sm={4}>
                    <TextFieldsOnBlur
                        fields={[
                            { name: `${startFieldName}${key}`, label, helperText: '', fullWidth, disabled }
                        ]}
                    />
                </Grid>
            ))}
        </Grid>
    );
};

export default memo(AddressFields);
